import { Box, Typography, Avatar, IconButton, keyframes } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { SaveBookmarkIcon, SaveBookmarkOutlineIcon } from '../icons/SaveBookmarkIcon';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DesignSystem from '../../designSystem';
import { useT } from '../../i18n/useT';

export interface FeedUser {
  id: string;
  name: string;
  username?: string;
  avatar?: string | null;
  rating: number;
}

export interface FeedProposal {
  id: string;
  title: string;
  description: string;
  budget: number;
  deadline: string;
  category: string;
  user: FeedUser;
  username?: string;
  tags: string[];
  location: string;
  postedAt: string;
  coverUrl?: string | null;
  favorite?: boolean;
}

interface ProposalListProps {
  proposals: FeedProposal[];
  onSaveToList?: (proposal: FeedProposal) => void;
}

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.98); filter: blur(2px); }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;

/** iOS-style feed cards: compact row + circular logo thumbnail (not full-bleed media). */
export default function ProposalList({ proposals, onSaveToList }: ProposalListProps) {
  const navigate = useNavigate();
  const t = useT();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const nextFresh = new Set<string>();
    for (const p of proposals) {
      if (!seenIdsRef.current.has(p.id)) {
        nextFresh.add(p.id);
        seenIdsRef.current.add(p.id);
      }
    }
    if (nextFresh.size > 0) {
      setFreshIds(nextFresh);
      const t = window.setTimeout(() => setFreshIds(new Set()), 900);
      return () => window.clearTimeout(t);
    }
  }, [proposals]);

  useEffect(() => {
    if (proposals.length === 0) {
      seenIdsRef.current = new Set();
      setFreshIds(new Set());
    }
  }, [proposals.length]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {proposals.map((proposal, index) => {
        const handle = (
          proposal.username ||
          proposal.user.username?.replace(/^@/, '') ||
          proposal.user.id
        ).replace(/^@/, '');
        const thumb = proposal.user.avatar || proposal.coverUrl || undefined;
        const location =
          proposal.location && proposal.location !== 'Unknown' ? proposal.location : null;
        const isFresh = freshIds.has(proposal.id);
        const freshIndex = isFresh
          ? Math.max(0, index - (proposals.length - freshIds.size))
          : 0;

        return (
          <Box
            key={proposal.id}
            component="article"
            onClick={() => navigate(`/${handle}`)}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.15,
              bgcolor: DesignSystem.colors.surface,
              borderRadius: DesignSystem.borderRadius.xl,
              border: 0,
              transition: `background-color ${DesignSystem.motion.duration} ${DesignSystem.motion.easing}`,
              '&:hover': { backgroundColor: DesignSystem.colors.surfaceElevated },
              ...(isFresh
                ? {
                    animation: `${cardIn} 0.5s ${DesignSystem.motion.easing} both`,
                    animationDelay: `${80 + freshIndex * 65}ms`,
                  }
                : null),
            }}
          >
            <Avatar
              src={thumb}
              alt={proposal.title}
              variant="circular"
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                bgcolor: DesignSystem.colors.surfaceElevated,
                fontSize: 22,
                fontWeight: 700,
                color: DesignSystem.colors.textPrimary,
                border: `2px solid ${DesignSystem.colors.border}`,
              }}
            >
              {(proposal.title || '?').charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: DesignSystem.colors.textPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.25,
                }}
              >
                {proposal.title}
              </Typography>
              {proposal.category && (
                <Typography
                  sx={{
                    mt: 0.2,
                    fontSize: 13,
                    color: DesignSystem.colors.textSecondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {proposal.category}
                </Typography>
              )}
              {location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, mt: 0.35 }}>
                  <LocationOn sx={{ fontSize: 13, color: DesignSystem.colors.primary }} />
                  <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textSecondary }}>
                    {location}
                  </Typography>
                </Box>
              )}
            </Box>

            <IconButton
              size="small"
              aria-label={t('saveToList')}
              onClick={(e) => {
                e.stopPropagation();
                if (onSaveToList) {
                  onSaveToList(proposal);
                  return;
                }
                navigate(`/${handle}`);
              }}
              sx={{
                color: proposal.favorite ? DesignSystem.colors.primary : DesignSystem.colors.textSecondary,
                flexShrink: 0,
              }}
            >
              {proposal.favorite ? (
                <SaveBookmarkIcon sx={{ fontSize: 22 }} />
              ) : (
                <SaveBookmarkOutlineIcon sx={{ fontSize: 22 }} />
              )}
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}
